import React, { useEffect, useState } from 'react';
import { useAuthStore } from '../lib/auth';
import { checklistTemplateAPI, checklistSubmissionAPI, venueAPI } from '../lib/api';
import { 
  ClipboardCheck, 
  Clock,
  CheckCircle2,
  XCircle,
  Play,
  Loader2,
  AlertCircle,
  MapPin,
  Camera,
  Thermometer,
  FileText
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';

const isTemplateUnlocked = (template) => {
  const currentHour = new Date().getHours();
  const unlockHour = Number(template?.unlock_hour ?? 0);
  const lockHour = Number(template?.lock_hour ?? 23);

  if (unlockHour <= lockHour) {
    return currentHour >= unlockHour && currentHour <= lockHour;
  }

  return currentHour >= unlockHour || currentHour <= lockHour;
};

// Checklist Execution Component
const ChecklistExecution = ({ template, onComplete }) => {
  const [items, setItems] = useState(
    (template.items || []).map(item => ({
      item_id: item.id,
      item_label: item.label,
      item_type: item.item_type,
      passed: null,
      value: '',
      failure_reason: '',
      photo_data: null,
      temp_min: item.temp_min,
      temp_max: item.temp_max,
    }))
  );
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const isFieldMissing = (value) => value === null || value === undefined || value === '';

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          setLocationError('Location access required. Please enable GPS.');
        },
        { enableHighAccuracy: true }
      );
    } else {
      setLocationError('Geolocation not supported by browser');
    }
  }, []);

  const updateItem = (index, updates) => {
    setItems(prev => {
      const newItems = [...prev];
      newItems[index] = { ...newItems[index], ...updates };
      return newItems;
    });
  };

  const compressImageFile = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Failed to read image'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('Invalid image file'));
      img.onload = () => {
        const maxDimension = 1280;
        const ratio = Math.min(1, maxDimension / Math.max(img.width, img.height));
        const width = Math.max(1, Math.round(img.width * ratio));
        const height = Math.max(1, Math.round(img.height * ratio));

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext('2d');

        if (!context) {
          reject(new Error('Image processing unavailable'));
          return;
        }

        context.drawImage(img, 0, 0, width, height);

        let quality = 0.8;
        let dataUrl = canvas.toDataURL('image/jpeg', quality);
        while (dataUrl.length > 750000 && quality > 0.45) {
          quality -= 0.1;
          dataUrl = canvas.toDataURL('image/jpeg', quality);
        }

        resolve(dataUrl);
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

  const handlePhotoUpload = async (index, e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const compressedImage = await compressImageFile(file);
        updateItem(index, { photo_data: compressedImage });
      } catch (error) {
        toast.error(error?.message || 'Unable to process image');
      }
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    const unanswered = items.filter(item => {
      if (item.item_type === 'temperature' || item.item_type === 'text') {
        return item.passed === null || isFieldMissing(item.value);
      }
      return item.passed === null;
    });
    if (unanswered.length > 0) {
      toast.error('Please answer all checklist items');
      return;
    }

    const failuresWithoutReason = items.filter(item => item.passed === false && isFieldMissing(item.failure_reason));
    if (failuresWithoutReason.length > 0) {
      toast.error('Please provide a reason for all failed items');
      return;
    }

    // Validate evidence for failed items - MANDATORY
    const failuresWithoutEvidence = items.filter(item => item.passed === false && !item.photo_data);
    if (failuresWithoutEvidence.length > 0) {
      toast.error('Failed items must have evidence photos attached');
      return;
    }

    // Validate evidence for Temperature/Fridge items - MANDATORY
    const tempOrFridgeItems = items.filter(item => 
      item.item_type === 'temperature' || item.item_label?.toLowerCase().includes('fridge')
    );
    const tempWithoutEvidence = tempOrFridgeItems.filter(item => !item.photo_data);
    if (tempWithoutEvidence.length > 0) {
      toast.error('Temperature/Fridge items require proof photos');
      return;
    }

    if (!location) {
      toast.error('Location is required to submit checklist');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        template_id: template.id,
        venue_id: template.venue_id,
        items: items.map(item => ({
          item_id: item.item_id,
          item_label: item.item_label,
          item_type: item.item_type,
          passed: item.passed,
          value: isFieldMissing(item.value) ? null : item.value,
          failure_reason: item.passed ? null : item.failure_reason,
          photo_data: item.photo_data,
        })),
        latitude: location.latitude,
        longitude: location.longitude,
      };

      await checklistSubmissionAPI.create(payload);
      toast.success('Checklist submitted successfully');
      onComplete();
    } catch (error) {
      const apiDetail = error?.response?.data?.detail;
      const errorMessage =
        (Array.isArray(apiDetail) ? apiDetail.map((d) => d?.msg).filter(Boolean).join(', ') : apiDetail) ||
        error?.message ||
        'Failed to submit checklist';
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const completedCount = items.filter(item => item.passed !== null).length;
  const progress = (completedCount / items.length) * 100;

  return (
    <div className="space-y-6 pb-24 form-mobile-safe">
      <div className="card-rtb">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[#A1A1AA] text-sm">Progress</span>
          <span className="text-white font-bold">{completedCount}/{items.length}</span>
        </div>
        <div className="w-full h-3 bg-[#27272A] rounded-full overflow-hidden">
          <div 
            className="h-full bg-[#FF6600] transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <div className={`p-4 rounded-md flex items-center gap-3 ${location ? 'bg-[#00E676]/10 border border-[#00E676]/50' : 'bg-[#FF3B30]/10 border border-[#FF3B30]/50'}`}>
        <MapPin className={`w-5 h-5 ${location ? 'text-[#00E676]' : 'text-[#FF3B30]'}`} />
        <span className={location ? 'text-[#00E676]' : 'text-[#FF3B30]'}>
          {location ? 'Location verified' : locationError || 'Getting location...'}
        </span>
      </div>

      <div className="space-y-4">
        {items.map((item, index) => (
          <div 
            key={item.item_id}
            className={`card-rtb ${
              item.passed === true ? 'border-[#00E676]/50' : 
              item.passed === false ? 'border-[#FF3B30]/50' : ''
            }`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                {item.item_type === 'temperature' && <Thermometer className="w-5 h-5 text-[#2979FF]" />}
                {item.item_type === 'photo' && <Camera className="w-5 h-5 text-[#FFB300]" />}
                {item.item_type === 'text' && <FileText className="w-5 h-5 text-[#A1A1AA]" />}
                {item.item_type === 'yes_no' && <ClipboardCheck className="w-5 h-5 text-[#FF6600]" />}
                <span className="text-white font-medium">{item.item_label}</span>
              </div>
              <span className="text-[#52525B] text-xs uppercase">{item.item_type.replace('_', '/')}</span>
            </div>

            {item.item_type === 'yes_no' && (
              <div className="flex gap-3">
                <Button
                  type="button"
                  onClick={(e) => { e.preventDefault(); updateItem(index, { passed: true }); }}
                  className={`flex-1 h-12 ${item.passed === true ? 'bg-[#00E676] text-black' : 'btn-rtb-secondary'}`}
                >
                  <CheckCircle2 className="w-5 h-5 mr-2" />
                  Yes / Pass
                </Button>
                <Button
                  type="button"
                  onClick={(e) => { e.preventDefault(); updateItem(index, { passed: false }); }}
                  className={`flex-1 h-12 ${item.passed === false ? 'bg-[#FF3B30] text-white' : 'btn-rtb-secondary'}`}
                >
                  <XCircle className="w-5 h-5 mr-2" />
                  No / Fail
                </Button>
              </div>
            )}

            {item.item_type === 'temperature' && (
              <div className="space-y-3">
                <Label htmlFor={`temp-value-${index}`} className="sr-only">Temperature value</Label>
                <div className="flex items-center gap-4">
                  <Input
                    id={`temp-value-${index}`}
                    name={`temp_value_${index}`}
                    type="number"
                    step="0.1"
                    value={item.value}
                    onChange={(e) => {
                      const rawValue = e.target.value;
                      if (isFieldMissing(rawValue)) {
                        updateItem(index, { value: '', passed: null });
                        return;
                      }

                      const temp = parseFloat(e.target.value);
                      if (Number.isNaN(temp)) {
                        updateItem(index, { value: rawValue, passed: null });
                        return;
                      }

                      // Temperature validation: if >15 or <0, auto-fail
                      let passed = temp >= (item.temp_min || -999) && temp <= (item.temp_max || 999);
                      if (temp > 15.0 || temp < 0.0) {
                        passed = false;
                        if (temp > 15.0 || temp < 0.0) {
                          toast.warning('Temperature Critical: Immediate Action Required.');
                        }
                      }
                      updateItem(index, { value: rawValue, passed });
                    }}
                    placeholder="Enter temperature"
                    className="input-rtb flex-1"
                  />
                  <span className="text-[#A1A1AA]">°C</span>
                </div>
              </div>
            )}

            {item.item_type === 'text' && (
              <>
                <Label htmlFor={`text-value-${index}`} className="sr-only">Text response</Label>
                <Textarea
                  id={`text-value-${index}`}
                  name={`text_value_${index}`}
                  value={item.value}
                  onChange={(e) => {
                    const textValue = e.target.value;
                    updateItem(index, { value: textValue, passed: isFieldMissing(textValue) ? null : true });
                  }}
                  placeholder="Enter details..."
                  className="input-rtb min-h-[100px]"
                />
              </>
            )}

            {item.item_type === 'photo' && (
              <div className="space-y-3">
                <input
                  type="file"
                  name={`photo_upload_${index}`}
                  accept="image/*"
                  capture="environment"
                  onChange={(e) => handlePhotoUpload(index, e)}
                  className="hidden"
                  id={`photo-${index}`}
                />
                <label
                  htmlFor={`photo-${index}`}
                  className="btn-rtb-secondary flex items-center justify-center gap-2 cursor-pointer w-full h-12"
                >
                  <Camera className="w-5 h-5" />
                  {item.photo_data ? 'Change Photo' : 'Take Photo'}
                </label>
                {item.photo_data && (
                  <img src={item.photo_data} alt="Captured" className="w-full h-48 object-cover rounded-md" />
                )}
              </div>
            )}

            {item.passed === false && (
              <div className="mt-4 p-4 bg-[#FF3B30]/10 border border-[#FF3B30]/50 rounded-md">
                <Label htmlFor={`failure-reason-${index}`} className="text-sm font-bold uppercase text-[#FF3B30] mb-2 block">Failure Reason</Label>
                <Textarea
                  id={`failure-reason-${index}`}
                  name={`failure_reason_${index}`}
                  value={item.failure_reason}
                  onChange={(e) => updateItem(index, { failure_reason: e.target.value })}
                  placeholder="Describe the issue..."
                  className="input-rtb min-h-[80px] border-[#FF3B30]/50"
                />
                {/* Mandatory evidence for failures */}
                <div className="mt-4 p-3 bg-[#FFB300]/10 border border-[#FFB300]/50 rounded-md">
                  <Label className="text-sm font-semibold uppercase text-[#FFB300] mb-2 block">Upload Evidence (Required)</Label>
                  <input
                    type="file"
                    name={`failure_photo_upload_${index}`}
                    accept="image/*"
                    capture="environment"
                    onChange={(e) => handlePhotoUpload(index, e)}
                    className="hidden"
                    id={`failure-photo-${index}`}
                  />
                  <label
                    htmlFor={`failure-photo-${index}`}
                    className="btn-rtb-secondary flex items-center justify-center gap-2 cursor-pointer w-full h-12"
                  >
                    <Camera className="w-5 h-5" />
                    {item.photo_data ? 'Change Evidence Photo' : 'Upload Evidence Photo'}
                  </label>
                  {item.photo_data && (
                    <img src={item.photo_data} alt="Evidence" className="w-full h-48 object-cover rounded-md mt-3" />
                  )}
                </div>
              </div>
            )}

            {/* Evidence required for Temperature/Fridge items */}
            {(item.item_type === 'temperature' || item.item_label?.toLowerCase().includes('fridge')) && (
              <div className="mt-4 p-4 bg-[#2979FF]/10 border border-[#2979FF]/50 rounded-md">
                <Label className="text-sm font-semibold uppercase text-[#2979FF] mb-2 block">Temperature Proof (Required)</Label>
                <input
                  type="file"
                  name={`temp_photo_upload_${index}`}
                  accept="image/*"
                  capture="environment"
                  onChange={(e) => handlePhotoUpload(index, e)}
                  className="hidden"
                  id={`temp-photo-${index}`}
                />
                <label
                  htmlFor={`temp-photo-${index}`}
                  className="btn-rtb-secondary flex items-center justify-center gap-2 cursor-pointer w-full h-12"
                >
                  <Camera className="w-5 h-5" />
                  {item.photo_data ? 'Change Temperature Proof' : 'Add Temperature Proof'}
                </label>
                {item.photo_data && (
                  <img src={item.photo_data} alt="Temperature Proof" className="w-full h-48 object-cover rounded-md mt-3" />
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      <Button
        type="button"
        onClick={handleSubmit}
        disabled={submitting || !location}
        className="btn-rtb-primary w-full"
      >
        {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Submit Checklist'}
      </Button>
    </div>
  );
};

const ChecklistsPage = () => {
  const [templates, setTemplates] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showExecution, setShowExecution] = useState(false);
  const availableTemplates = (templates || []).filter(isTemplateUnlocked);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Wrapped individual calls to ensure one failure doesn't kill the whole page
      const [templatesRes, submissionsRes, venuesRes] = await Promise.all([
        checklistTemplateAPI.list().catch(() => ({ data: [] })),
        checklistSubmissionAPI.list().catch(() => ({ data: [] })),
        venueAPI.list().catch(() => ({ data: [] })),
      ]);
      
      setTemplates(templatesRes?.data || []);
      setSubmissions(submissionsRes?.data || []);
      setVenues(venuesRes?.data || []);
    } catch (error) {
      console.error('Fetch error:', error);
      const errorMessage = error?.message || JSON.stringify(error) || 'Partial load: Server connection unstable';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-[#FF6600]" /></div>;

  if (showExecution && selectedTemplate) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white uppercase">{selectedTemplate.name}</h1>
          <Button type="button" onClick={() => setShowExecution(false)} className="btn-rtb-secondary">Cancel</Button>
        </div>
        <ChecklistExecution template={selectedTemplate} onComplete={() => { setShowExecution(false); fetchData(); }} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-white uppercase">Checklists</h1>
      
      <Tabs defaultValue="templates">
        <TabsList className="bg-[#1E1E1E] border border-[#3F3F46]">
          <TabsTrigger value="templates">Templates</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="templates" className="space-y-4">
          {availableTemplates.length > 0 ? (
            availableTemplates.map((template) => {
              const venue = venues?.find(v => v.id === template.venue_id);
              
              return (
                <div key={template.id} className="card-rtb flex items-center justify-between">
                  <div>
                    <h3 className="text-white font-semibold">{template.name}</h3>
                    <p className="text-[#52525B] text-sm">
                      {venue?.name || 'Unassigned Venue'} • {template.items?.length || 0} items
                    </p>
                  </div>
                  <Button
                    type="button"
                    onClick={(e) => { 
                      e.preventDefault(); 
                      e.stopPropagation(); 
                      setSelectedTemplate(template); 
                      setShowExecution(true); 
                    }}
                    className="btn-rtb-primary"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Start
                  </Button>
                </div>
              );
            })
          ) : (
            <div className="card-rtb text-center py-12">
              <AlertCircle className="w-12 h-12 text-[#3F3F46] mx-auto mb-4" />
              <h3 className="text-white font-bold uppercase">No Checklists Available</h3>
              <p className="text-[#A1A1AA]">No templates are currently within their active time window.</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="history" className="space-y-4">
           {/* History mapping similar to templates... */}
           {submissions && submissions.length > 0 ? (
             submissions.map(sub => (
               <div key={sub.id} className="card-rtb">
                 <h3 className="text-white">{sub.template_name}</h3>
                 <p className="text-sm text-[#A1A1AA]">{new Date(sub.created_at).toLocaleString()}</p>
               </div>
             ))
           ) : (
             <div className="card-rtb text-center py-12">
               <p className="text-[#A1A1AA]">No submission history.</p>
             </div>
           )}
        </TabsContent>
      </Tabs>
      
      <Dialog open={false}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Aria Placeholder</DialogTitle>
            <DialogDescription>Satisfying browser accessibility warnings.</DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChecklistsPage;